import MarkdownIt from 'markdown-it';
import { DOMSerializer, Schema } from 'prosemirror-model';
import { MarkdownParser, MarkdownSerializer, defaultMarkdownSerializer } from 'prosemirror-markdown';
import { editorNodeType } from 'shared/constants.json';
import { logCatch } from '@/lib/logger/logger';

const todoItemNodeType = 'todo_item';

const cell = (tag) => ({
  content: 'inline*',
  attrs: { align: { default: null } },
  isolating: true,
  toDOM: (node) => [tag, { align: node.attrs.align }, 0],
});

// the editor keeps only what its own schema declares, so every node and mark here is named and
// shaped the way the editor's extensions parse it back
const markdownSchema = new Schema({
  nodes: {
    doc: { content: 'block+' },
    paragraph: { content: 'inline*', group: 'block', toDOM: () => ['p', 0] },
    heading: {
      attrs: { level: { default: 1 } },
      content: 'inline*',
      group: 'block',
      toDOM: (node) => [`h${node.attrs.level}`, 0],
    },
    blockquote: { content: 'block+', group: 'block', toDOM: () => ['blockquote', 0] },
    code_block: {
      content: 'text*',
      marks: '',
      group: 'block',
      code: true,
      toDOM: () => ['pre', ['code', 0]],
    },
    horizontal_rule: { group: 'block', toDOM: () => ['hr'] },
    bullet_list: { content: 'list_item+', group: 'block', toDOM: () => ['ul', 0] },
    ordered_list: {
      attrs: { order: { default: 1 } },
      content: 'list_item+',
      group: 'block',
      toDOM: (node) => ['ol', { start: node.attrs.order === 1 ? null : node.attrs.order }, 0],
    },
    list_item: { content: 'paragraph block*', defining: true, toDOM: () => ['li', 0] },
    todo_list: {
      content: 'todo_item+',
      group: 'block',
      toDOM: () => ['ul', { 'data-type': editorNodeType.todoList }, 0],
    },
    todo_item: {
      attrs: { done: { default: false } },
      content: '(paragraph|todo_list)+',
      defining: true,
      toDOM: (node) => ['li', { 'data-type': todoItemNodeType, 'data-done': String(node.attrs.done) }, 0],
    },
    table: { content: 'table_row+', group: 'block', isolating: true, toDOM: () => ['table', ['tbody', 0]] },
    table_row: { content: '(table_cell | table_header)*', toDOM: () => ['tr', 0] },
    table_cell: cell('td'),
    table_header: cell('th'),
    image: {
      attrs: { src: {}, alt: { default: null }, title: { default: null } },
      group: 'inline',
      inline: true,
      toDOM: (node) => ['img', node.attrs],
    },
    hard_break: { group: 'inline', inline: true, selectable: false, toDOM: () => ['br'] },
    emoji: {
      attrs: { emoji: { default: '' } },
      group: 'inline',
      inline: true,
      atom: true,
      toDOM: (node) => ['span', { class: 'emoji' }, node.attrs.emoji],
    },
    mention: {
      attrs: { id: {}, label: {} },
      group: 'inline',
      inline: true,
      atom: true,
      toDOM: (node) => ['span', { class: 'mention' }, `@${node.attrs.label}`],
    },
    goalMention: {
      attrs: { id: {}, title: {}, icon: { default: '' } },
      group: 'inline',
      inline: true,
      atom: true,
      toDOM: (node) => ['span', { class: 'goal-mention' }, node.attrs.title],
    },
    text: { group: 'inline' },
  },
  marks: {
    link: {
      attrs: { href: { default: null } },
      inclusive: false,
      toDOM: (node) => ['a', { href: node.attrs.href }, 0],
    },
    bold: { toDOM: () => ['strong', 0] },
    italic: { toDOM: () => ['em', 0] },
    strike: { toDOM: () => ['s', 0] },
    underline: { toDOM: () => ['u', 0] },
    code: { toDOM: () => ['code', 0] },
  },
});

const TASK_ITEM = /^\[([ xX])\]\s+/;

const closeIndexOf = (tokens, openIndex) => {
  const { level } = tokens[openIndex];
  return tokens.findIndex((token, index) => index > openIndex
    && token.level === level
    && token.nesting === -1);
};

// markdown-it has no task lists, and the editor's todo nodes are a list type of their own rather
// than a checkbox inside a plain item, so the tokens are renamed before the parser sees them. A
// list that mixes the two becomes todo items throughout, since a todo list holds nothing else
const taskLists = (md) => {
  md.core.ruler.after('inline', 'todo_lists', (state) => {
    const { tokens } = state;
    const lists = [];
    const openList = () => (lists.length === 0 ? null : lists[lists.length - 1]);

    const toTodoList = (list, closeToken) => {
      tokens[list.index].type = 'todo_list_open';
      closeToken.type = 'todo_list_close';
      list.items.forEach((item) => {
        tokens[item.index].type = 'todo_item_open';
        tokens[item.index].attrSet('data-done', String(item.done));
        tokens[closeIndexOf(tokens, item.index)].type = 'todo_item_close';
      });
    };

    tokens.forEach((token, index) => {
      if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') {
        lists.push({ index, bullet: token.type === 'bullet_list_open', items: [], hasTask: false });
        return;
      }
      if (token.type === 'bullet_list_close' || token.type === 'ordered_list_close') {
        const list = lists.pop();
        if (list.hasTask) {
          toTodoList(list, token);
        }
        return;
      }
      if (token.type === 'list_item_open') {
        openList()?.items.push({ index, done: false });
        return;
      }

      const list = openList();
      if (token.type !== 'inline' || list === null || !list.bullet || tokens[index - 2]?.type !== 'list_item_open') {
        return;
      }
      const match = TASK_ITEM.exec(token.content);
      if (match === null) {
        return;
      }

      list.hasTask = true;
      list.items[list.items.length - 1].done = match[1] !== ' ';

      token.content = token.content.slice(match[0].length);
      const [first] = token.children;
      if (first?.type === 'text') {
        first.content = first.content.slice(match[0].length);
      }
    });

    return true;
  });
};

const UNDERLINE_TAG = /^<(\/?)u>/i;

// markdown has no underline, so the serializer emits the tag markdown leaves to html; this reads
// that one tag back without turning raw html on for everything else
const underlineTag = (md) => {
  md.inline.ruler.before('text', 'underline_tag', (state, silent) => {
    // the rule is tried at every position, and the source is a whole answer
    if (state.src[state.pos] !== '<') {
      return false;
    }

    const match = UNDERLINE_TAG.exec(state.src.slice(state.pos));
    if (match === null) {
      return false;
    }

    if (!silent) {
      const closing = match[1] !== '';
      state.push(closing ? 'u_close' : 'u_open', 'u', closing ? -1 : 1);
    }
    state.pos += match[0].length;

    return true;
  });
};

// breaks, so a single newline in an answer is the line break it looks like; no html, so anything
// the model writes as a tag arrives as the text it typed rather than as markup
const tokenizer = MarkdownIt('default', { html: false, breaks: true, linkify: false })
  .use(taskLists)
  .use(underlineTag);

// a link typed or pasted without a scheme is a relative path to the browser, so it would
// resolve against the app and navigate away from it instead of leaving for the host
const absoluteHref = (href) => {
  const looksLikeHost = /^[^/\s?#]+\.[^/\s?#]+/.test(href);
  const hasScheme = /^[a-z][a-z0-9+-]*:/i.test(href);
  if (!looksLikeHost || hasScheme || href.startsWith('//')) {
    return href;
  }

  return `https://${href}`;
};

const alignOf = (token) => {
  const match = /text-align:\s*(left|center|right)/.exec(token.attrGet('style') ?? '');
  return { align: match === null ? null : match[1] };
};

const parser = new MarkdownParser(markdownSchema, tokenizer, {
  paragraph: { block: 'paragraph' },
  heading: { block: 'heading', getAttrs: (token) => ({ level: Number(token.tag.slice(1)) }) },
  blockquote: { block: 'blockquote' },
  code_block: { block: 'code_block', noCloseToken: true },
  fence: { block: 'code_block', noCloseToken: true },
  hr: { node: 'horizontal_rule' },
  bullet_list: { block: 'bullet_list' },
  ordered_list: { block: 'ordered_list', getAttrs: (token) => ({ order: Number(token.attrGet('start') ?? 1) }) },
  list_item: { block: 'list_item' },
  todo_list: { block: 'todo_list' },
  todo_item: { block: 'todo_item', getAttrs: (token) => ({ done: token.attrGet('data-done') === 'true' }) },
  table: { block: 'table' },
  thead: { ignore: true },
  tbody: { ignore: true },
  tr: { block: 'table_row' },
  th: { block: 'table_header', getAttrs: alignOf },
  td: { block: 'table_cell', getAttrs: alignOf },
  image: {
    node: 'image',
    getAttrs: (token) => ({
      src: token.attrGet('src'),
      title: token.attrGet('title'),
      alt: token.children?.[0]?.content ?? null,
    }),
  },
  hardbreak: { node: 'hard_break' },
  softbreak: { node: 'hard_break' },
  em: { mark: 'italic' },
  strong: { mark: 'bold' },
  s: { mark: 'strike' },
  u: { mark: 'underline' },
  code_inline: { mark: 'code', noCloseToken: true },
  link: { mark: 'link', getAttrs: (token) => ({ href: absoluteHref(token.attrGet('href') ?? '') }) },
});

const domSerializer = DOMSerializer.fromSchema(markdownSchema);

const emptyDocument = { type: 'doc', content: [{ type: 'paragraph' }] };

const plainDocument = (text) => {
  const blocks = text.split(/\n{2,}/)
    .filter((block) => block.trim() !== '')
    .map((block) => ({ type: 'paragraph', content: [{ type: 'text', text: block }] }));

  return blocks.length === 0 ? emptyDocument : { type: 'doc', content: blocks };
};

// the editor builds the document against its own schema, which has to declare every node and mark
// this one can produce — an attribute it does not know it drops, a node type it does not know
// costs the whole document
export const markdownToDoc = (text) => {
  if (typeof text !== 'string' || text.trim() === '') {
    return emptyDocument;
  }

  try {
    return parser.parse(text).toJSON();
  } catch (error) {
    // an answer this cannot shape is still worth reading as what was written, and the render
    // that fails on it is the conversation's, not this message's
    return logCatch(() => plainDocument(text))(error);
  }
};

// for the clipboard, where a document is only worth what the app it is pasted into can read
export const docToHtml = (doc) => {
  const container = document.createElement('div');
  container.appendChild(domSerializer.serializeFragment(markdownSchema.nodeFromJSON(doc).content));

  return container.innerHTML;
};

const separatorFor = (align) => {
  switch (align) {
    case 'left':
      return ':---';
    case 'center':
      return ':---:';
    case 'right':
      return '---:';
    default:
      return '---';
  }
};

const serializer = new MarkdownSerializer({
  paragraph: defaultMarkdownSerializer.nodes.paragraph,
  heading: defaultMarkdownSerializer.nodes.heading,
  blockquote: defaultMarkdownSerializer.nodes.blockquote,
  image: defaultMarkdownSerializer.nodes.image,
  ordered_list: defaultMarkdownSerializer.nodes.ordered_list,
  code_block: (state, node) => {
    state.write('```\n');
    state.text(node.textContent, false);
    state.ensureNewLine();
    state.write('```');
    state.closeBlock(node);
  },
  horizontal_rule: (state, node) => {
    state.write('---');
    state.closeBlock(node);
  },
  bullet_list: (state, node) => state.renderList(node, '  ', () => '- '),
  list_item: (state, node) => state.renderContent(node),
  todo_list: (state, node) => state.renderList(node, '  ', (index) => (node.child(index).attrs.done ? '- [x] ' : '- [ ] ')),
  todo_item: (state, node) => state.renderContent(node),
  table: (state, node) => {
    node.forEach((row, _, rowIndex) => {
      state.write('|');
      row.forEach((column) => {
        state.write(' ');
        state.renderInline(column);
        state.write(' |');
      });
      state.ensureNewLine();

      if (rowIndex === 0) {
        state.write('|');
        row.forEach((column) => state.write(` ${separatorFor(column.attrs.align)} |`));
        state.ensureNewLine();
      }
    });
    state.closeBlock(node);
  },
  // the renderer turns a single newline into a break, so the answer keeps the line the composer
  // showed; one that ends a block has nothing left to break and would read as a new paragraph
  hard_break: (state, node, parent, index) => {
    for (let i = index + 1; i < parent.childCount; i += 1) {
      if (parent.child(i).type !== node.type) {
        state.write('\n');
        return;
      }
    }
  },
  emoji: (state, node) => state.text(node.attrs.emoji, false),
  mention: (state, node) => state.text(`@${node.attrs.label}`, false),
  goalMention: (state, node) => state.text(node.attrs.title, false),
  text: defaultMarkdownSerializer.nodes.text,
}, {
  bold: { open: '**', close: '**', mixable: true, expelEnclosingWhitespace: true },
  italic: { open: '_', close: '_', mixable: true, expelEnclosingWhitespace: true },
  strike: { open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true },
  underline: { open: '<u>', close: '</u>', mixable: true },
  code: defaultMarkdownSerializer.marks.code,
  link: {
    open: (state, mark) => (mark.attrs.href === null ? '' : '['),
    close: (state, mark) => (mark.attrs.href === null ? '' : `](${mark.attrs.href})`),
  },
});

// marks are written in the order the node lists them: a link belongs around the formatting rather
// than inside it, and code is a literal span that the other delimiters have no meaning inside of
const orderedMarks = (marks) => {
  if (marks.some((value) => value.type === 'code')) {
    return marks.filter((value) => value.type === 'code');
  }

  return [...marks].sort((a, b) => Number(b.type === 'link') - Number(a.type === 'link'));
};

const withOrderedMarks = (node) => ({
  ...node,
  ...(node.marks === undefined ? {} : { marks: orderedMarks(node.marks) }),
  ...(node.content === undefined ? {} : { content: node.content.map(withOrderedMarks) }),
});

export const docToMarkdown = (doc) => {
  if (doc === null || typeof doc !== 'object') {
    return '';
  }

  try {
    // strict: false, so a node the editor gained but this schema has not yet costs its formatting
    // rather than the whole message
    return serializer
      .serialize(markdownSchema.nodeFromJSON(withOrderedMarks(doc)), { tightLists: true, strict: false })
      .trim();
  } catch (error) {
    // the composer reads this on every keystroke, so a document this cannot shape costs the draft
    // rather than the panel it is typed in
    return logCatch(() => '')(error);
  }
};
